import React, { useState, useEffect, useRef } from "react";
import { Alert, Card, Form, Input, DatePicker, Select, Button, message, Row, Col, Typography, Modal, TimePicker, Drawer, Divider, Badge, Tooltip, Space, InputNumber, Spin, Checkbox, Radio, Tag } from "antd";
import { shouldKeepPaperRotations } from "../../AirlineScheduleIngest/ingestListPresentation";
import { SettingOutlined, DatabaseOutlined, CalendarOutlined, NumberOutlined, CarOutlined, MenuOutlined, SafetyOutlined, EditOutlined, ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import enhancedApi from "../../../../api/enhancedApi";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import ConstraintsService from "../services/ConstraintsService";
import CurfewRulesViewer from "./constraints/CurfewRulesViewer";
import MinGroundTimeViewer from "./constraints/MinGroundTimeViewer";
import { UTCDatePicker, UTCTimePicker, UTCDateRangePicker } from "../../../../components/DateTimeInput";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { useAuth } from "../../../../hooks/useAuth";
import ScheduleTagSelect from "../../shared/ScheduleTagSelect";
import { useScheduleTags } from "../../shared/useScheduleTags";
import ScheduleTagManageModal from "./ScheduleTagManageModal";
import { getAirportConstraint, normalizeAirportCode } from "../../../../utils/airportCodeUtils";

// const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const getFlightDepartureAirport = (flight) => flight?.departure_airport || flight?.dep_airport;
const getFlightArrivalAirport = (flight) => flight?.arrival_airport || flight?.arr_airport;

// 创建全局表单注册机制
if (!window.antdForms) {
    window.antdForms = {};
}

/**
 * 航班计划基本信息组件
 */
export const ScheduleBasicInfo = () => {
    const { state, updateSchedule, updateParameters, setConstraintsLoading, setAirportConstraints, setConstraintsError, updateAircraftInFlights } = useScheduleEditor();
    const {
        schedule,
        parameters,
        flights,
        constraints: { airportConstraints, loading: constraintsLoading },
    } = state;
    const { timeZone } = useTimeZone();
    const { hasPermission } = useAuth();
    const { definitions: scheduleTagDefinitions, definitionMap: scheduleTagDefinitionMap, isLoading: scheduleTagsLoading, loadError: scheduleTagLoadError } = useScheduleTags({
        includeInactive: true,
    });

    const [form] = Form.useForm();
    const [airportOptions, setAirportOptions] = useState([]);
    const [loadingAirports, setLoadingAirports] = useState(false);
    const formId = "scheduleBasicInfoForm";

    // 抽屉状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [tagManageVisible, setTagManageVisible] = useState(false);
    const initialLoadDoneRef = useRef(false);
    const inFlightAirportCodesRef = useRef(new Set());

    // 注册表单实例到全局
    useEffect(() => {
        window.antdForms[formId] = form;

        return () => {
            // 组件卸载时取消注册
            delete window.antdForms[formId];
        };
    }, [form]);

    // 获取机场列表 - 使用已有数据而不是重新请求
    useEffect(() => {
        const fetchAirports = async () => {
            try {
                setLoadingAirports(true);
                // 先检查全局存储中是否已有机场数据
                if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
                    setAirportOptions(window.cachedAirports);
                    setLoadingAirports(false);
                    return;
                }

                // 如果没有，再请求
                const response = await enhancedApi.masterData.airport.getAirportList({ page_size: 500 }); // 添加 page_size
                // 从 response.data.items 获取列表数据，并进行空值检查
                const airportList = response?.data?.items || [];

                if (airportList.length > 0) {
                    // 缓存到全局
                    window.cachedAirports = airportList;
                    setAirportOptions(airportList);
                }
            } catch (error) {
                console.error("获取机场列表失败:", error);
                message.error("获取机场列表失败");
            } finally {
                setLoadingAirports(false);
            }
        };

        fetchAirports();
    }, []);

    // 页面加载时加载约束数据，新机场出现时增量补拉
    useEffect(() => {
        if (constraintsLoading) return;
        if (!schedule?.id && !schedule?.name && (!flights || flights.length === 0)) return;

        const getAirportCodes = () => {
            const codes = new Set();

            if (parameters.available_segments && parameters.available_segments.length > 0) {
                parameters.available_segments.forEach((segment) => {
                    const dep = normalizeAirportCode(segment.departure_airport);
                    const arr = normalizeAirportCode(segment.arrival_airport);
                    if (dep) codes.add(dep);
                    if (arr) codes.add(arr);
                });
            }

            if (flights && flights.length > 0) {
                flights.forEach((flight) => {
                    const dep = normalizeAirportCode(getFlightDepartureAirport(flight));
                    const arr = normalizeAirportCode(getFlightArrivalAirport(flight));
                    if (dep) codes.add(dep);
                    if (arr) codes.add(arr);
                });
            }

            return Array.from(codes);
        };

        const airportCodes = getAirportCodes();
        const isInitialLoad = !initialLoadDoneRef.current && Object.keys(airportConstraints).length === 0;

        if (isInitialLoad) {
            initialLoadDoneRef.current = true;
            const loadInitialConstraints = async () => {
                setConstraintsLoading(true);
                try {
                    if (airportCodes.length > 0) {
                        await ConstraintsService.loadAirportConstraints(airportCodes, {
                            setConstraintsLoading,
                            setAirportConstraints,
                            setConstraintsError,
                        });
                    } else {
                        await ConstraintsService.loadAllAirportConstraints({
                            setConstraintsLoading,
                            setAirportConstraints,
                            setConstraintsError,
                        });
                    }
                } catch (error) {
                    console.error("加载约束数据失败:", error);
                    message.error("加载约束数据失败");
                } finally {
                    setConstraintsLoading(false);
                }
            };

            loadInitialConstraints();
            return;
        }

        // 增量加载：过滤掉已存在的、以及正在请求中的机场代码
        const missingCodes = airportCodes.filter(
            (code) => !airportConstraints[code] && !inFlightAirportCodesRef.current.has(code)
        );
        if (missingCodes.length === 0) return;

        missingCodes.forEach((code) => inFlightAirportCodesRef.current.add(code));

        // 本 effect 以 airportConstraints 为依赖，又会写入它：只有每个 missingCode 都落到
        // state 上，下一轮才会收敛。因此无论请求成败，都必须为缺口补齐兜底值，否则会无限重拉。
        // 兜底值只保证“这个机场查过了”，最小过站保持为空，不得写成 60 分钟。
        const withFallbackForMissing = (loaded) => {
            const filled = { ...loaded };
            missingCodes.forEach((code) => {
                if (filled[code]) return;
                filled[code] = {
                    iata_code: code,
                    airport_name: code,
                    timezone: "UTC",
                    dep_curfew_rules: [],
                    arr_curfew_rules: [],
                    turnaround_time: null,
                    is_maintenance_base: false,
                };
            });
            return filled;
        };

        const loadMissingConstraints = async () => {
            setConstraintsLoading(true);
            let loaded = {};
            try {
                loaded = await ConstraintsService.fetchAirportConstraints(missingCodes);
            } catch (error) {
                console.error("增量加载约束数据失败:", error);
            } finally {
                setAirportConstraints((prev) => ({ ...prev, ...withFallbackForMissing(loaded) }));
                missingCodes.forEach((code) => inFlightAirportCodesRef.current.delete(code));
                setConstraintsLoading(false);
            }
        };

        loadMissingConstraints();
    }, [schedule?.id, schedule?.name, parameters, flights, airportConstraints, constraintsLoading, setAirportConstraints, setConstraintsError, setConstraintsLoading]);

    // 回到页面（窗口重新获得焦点）时按当前航班列表刷新机场约束：
    // 运控的动线是“编排页开着 → 去主数据改最小过站 → 切回来”，
    // 表格展示和后续自动填时刻都要用改过之后的值，不必先点“刷新基础数据”。
    // 失败时静默保留已加载的数据，由后续操作（加航段/导入/重算）再次尝试。
    const flightsForFocusRefreshRef = useRef(flights);
    flightsForFocusRefreshRef.current = flights;

    useEffect(() => {
        const refreshConstraintsOnFocus = () => {
            const codes = new Set();
            (flightsForFocusRefreshRef.current || []).forEach((flight) => {
                const dep = normalizeAirportCode(getFlightDepartureAirport(flight));
                const arr = normalizeAirportCode(getFlightArrivalAirport(flight));
                if (dep) codes.add(dep);
                if (arr) codes.add(arr);
            });
            const airportCodes = Array.from(codes);
            if (airportCodes.length === 0) {
                return;
            }

            ConstraintsService.fetchAirportConstraints(airportCodes)
                .then((freshConstraints) => {
                    setAirportConstraints((prev) => ({ ...prev, ...freshConstraints }));
                })
                .catch((error) => {
                    console.error("回到页面刷新机场约束失败:", error);
                });
        };

        window.addEventListener("focus", refreshConstraintsOnFocus);
        return () => window.removeEventListener("focus", refreshConstraintsOnFocus);
    }, [setAirportConstraints]);

    // 初始化表单
    useEffect(() => {
        if (schedule) {
            try {
                const formValues = {
                    name: schedule.name || "",
                    date_range: schedule.start_date && schedule.end_date ? [dayjs(schedule.start_date), dayjs(schedule.end_date)] : null,
                    aircraft_id: schedule.aircraft_id,
                    flight_number_prefix: parameters.flight_number_prefix || "",
                    min_ground_time: parameters.min_ground_time || 60,
                    tags: schedule.tags || [],
                };

                form.setFieldsValue(formValues);

                // 这里手动触发表单验证，确保所有字段状态正确
                form.validateFields().catch(() => {
                    // 忽略验证错误，我们只是想触发验证
                });
            } catch (error) {
                console.error("设置表单值错误:", error);
            }
        }
    }, [schedule, parameters, form]);

    // 字段值变化时保存
    const handleValuesChange = (changedValues, allValues) => {
        // 构建对应的更新对象
        try {
            // 处理基本信息更新
            if ("name" in changedValues || "date_range" in changedValues || "tags" in changedValues) {
                const scheduleUpdates = {};

                if ("name" in changedValues) {
                    scheduleUpdates.name = allValues.name || "";
                }

                if ("date_range" in changedValues && allValues.date_range && allValues.date_range.length === 2) {
                    const dateData = {
                        start_date: allValues.date_range[0],
                        end_date: allValues.date_range[1],
                    };
                    scheduleUpdates.start_date = dateData.start_date;
                    scheduleUpdates.end_date = dateData.end_date;
                }

                // 注意：aircraft_id 的更新已由 AircraftSelector 的 onChange 事件处理
                // 这里不再处理 aircraft_id，避免覆盖掉完整的飞机对象信息

                if ("tags" in changedValues) {
                    scheduleUpdates.tags = allValues.tags || [];
                }

                if (Object.keys(scheduleUpdates).length > 0) {
                    updateSchedule(scheduleUpdates);
                }
            }

            // 单独处理参数更新
            if ("flight_number_prefix" in changedValues || "min_ground_time" in changedValues) {
                const paramUpdates = {
                    // 保留现有参数
                    ...parameters,
                };

                if ("flight_number_prefix" in changedValues) {
                    paramUpdates.flight_number_prefix = allValues.flight_number_prefix || "";
                }

                if ("min_ground_time" in changedValues) {
                    paramUpdates.min_ground_time = parseInt(allValues.min_ground_time) || 60;
                }

                updateParameters(paramUpdates);
            }
        } catch (error) {
            console.error("处理字段变化错误:", error);
        }
    };

    // 打开高级设置抽屉
    const openDrawer = () => {
        setDrawerVisible(true);
    };

    // 约束数据加载状态提示
    const renderConstraintsLoadingStatus = () => {
        if (constraintsLoading) {
            return (
                <div style={{ marginTop: 8, fontSize: 12, color: "#1890ff" }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    正在加载约束数据...
                </div>
            );
        }
        if (Object.keys(airportConstraints).length > 0) {
            return (
                <div style={{ marginTop: 8, fontSize: 12, color: "#52c41a" }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {parameters.transportation_demands.length}个运输需求、{Object.keys(airportConstraints).length}个机场约束
                </div>
            );
        }
        return null;
    };

    return (
        <Card
            className="schedule-basic-info"
            title={
                <Space>
                    <SettingOutlined /> 计划基本信息
                </Space>
            }
        >
            <Form
                id={formId}
                form={form}
                layout="vertical"
                requiredMark={false}
                onValuesChange={handleValuesChange}
                initialValues={{
                    name: schedule.name,
                    date_range: schedule.start_date && schedule.end_date ? [dayjs(schedule.start_date), dayjs(schedule.end_date)] : null,
                    aircraft_id: schedule.aircraft_id,
                    flight_number_prefix: parameters.flight_number_prefix || "",
                    min_ground_time: parameters.min_ground_time || 60,
                    tags: schedule.tags || [],
                }}
            >
                <style>{`
                    .info-item {
                        margin-bottom: 8px;
                    }
                    .ant-form-item {
                        margin-bottom: 16px;
                    }
                    .form-inline-group {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 16px;
                        margin-bottom: 16px;
                    }
                    .form-inline-group .ant-form-item {
                        flex: 1;
                        min-width: 200px;
                        margin-bottom: 0;
                    }
                    .day-actions {
                        display: flex;
                        margin-top: 8px;
                        gap: 8px;
                    }
                    .day-actions button {
                        font-size: 12px;
                        padding: 0 8px;
                        height: 24px;
                    }
                    .weekday-picker {
                        width: 100%;
                    }
                    .weekday-picker .ant-checkbox-group {
                        display: flex;
                        flex-wrap: wrap;
                        row-gap: 8px;
                    }
                    .weekday-picker .ant-checkbox-wrapper {
                        margin-right: 12px;
                    }
                    @media (max-width: 768px) {
                        .weekday-picker .ant-checkbox-wrapper {
                            margin-right: 8px;
                        }
                    }
                `}</style>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="name" label="计划名称" rules={[{ required: true, message: "请输入计划名称" }]}>
                            <Input placeholder="输入航班计划名称" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="date_range" label="计划日期范围 (UTC)" rules={[{ required: true, message: "请选择计划日期范围" }]}>
                            <UTCDateRangePicker style={{ width: "100%" }} placeholder={["开始日期 (UTC)", "结束日期 (UTC)"]} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="aircraft_id" label="执行飞机" rules={[{ required: true, message: "请选择执行飞机" }]}>
                            <AircraftSelector
                                placeholder="选择执行飞机"
                                valueType="id"
                                displayMode="detailed"
                                onChange={(value, option) => {
                                    // 获取完整的飞机对象信息
                                    if (option && option.aircraft) {
                                        const selectedAircraft = option.aircraft;
                                        // 更新schedule中的aircraft字段
                                        updateSchedule({
                                            aircraft_id: value,
                                            aircraft: selectedAircraft,
                                        });

                                        // 同时更新所有航班的飞机注册号
                                        updateAircraftInFlights(selectedAircraft.registration);

                                        // 提示用户
                                        message.success(`已将所有航班的飞机更新为 ${selectedAircraft.registration}`);
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="高级设置" style={{ marginBottom: "8px" }}>
                            <Button type="primary" ghost icon={<MenuOutlined />} onClick={openDrawer} style={{ width: "100%" }}>
                                约束、需求设置
                            </Button>
                            {renderConstraintsLoadingStatus()}
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="flight_number_prefix" label="航班号前缀" tooltip="航班号前缀，通常是航司两字代码，如MU、CZ等">
                            <Input placeholder="输入航班号前缀" maxLength={3} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            name="tags"
                            label={
                                <Space size={4}>
                                    <span>计划标签</span>
                                    {hasPermission("schedule:tag:manage") ? (
                                        <Tooltip title="管理全局标签">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<SettingOutlined />}
                                                onClick={() => setTagManageVisible(true)}
                                                aria-label="管理全局标签"
                                            />
                                        </Tooltip>
                                    ) : null}
                                </Space>
                            }
                        >
                            <ScheduleTagSelect
                                definitions={scheduleTagDefinitions}
                                definitionMap={scheduleTagDefinitionMap}
                                isLoading={scheduleTagsLoading}
                                loadError={scheduleTagLoadError}
                            />
                        </Form.Item>
                        {scheduleTagLoadError ? <Text type="danger">标签颜色配置未加载，计划仍可继续编辑和保存。</Text> : null}
                    </Col>
                </Row>
            </Form>

            {/* 高级设置抽屉 */}
            <Drawer title="航班计划高级设置" placement="right" width={800} onClose={() => setDrawerVisible(false)} open={drawerVisible}>
                <div>
                    <h3 style={{ marginBottom: "16px", display: "flex", alignItems: "center" }}>
                        <SafetyOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                        <span>宵禁约束管理</span>
                    </h3>
                    <ConstraintsPanelContent />

                    <Divider style={{ margin: "32px 0" }} />

                    <h3 style={{ marginBottom: "16px", display: "flex", alignItems: "center" }}>
                        <DatabaseOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                        <span>运输需求</span>
                    </h3>
                    <TransportationDemandContent airportOptions={airportOptions} loadingAirports={loadingAirports} />
                </div>
            </Drawer>
            <ScheduleTagManageModal open={tagManageVisible} onClose={() => setTagManageVisible(false)} />
        </Card>
    );
};

/**
 * 约束管理面板内容组件 - 直接实现
 */
const ConstraintsPanelContent = () => {
    const { state, setConstraintsLoading, setAirportConstraints, setConstraintsError, updateParameters } = useScheduleEditor();

    const {
        flights,
        parameters,
        constraints: { airportConstraints, loading, error },
    } = state;

    const [form] = Form.useForm();
    const [showCurfewModal, setShowCurfewModal] = useState(false);
    const [showGroundTimeModal, setShowGroundTimeModal] = useState(false);
    const [selectedAirport, setSelectedAirport] = useState(null);

    // 从可选航段中收集所有机场
    const airportCodes = React.useMemo(() => {
        const codes = new Set();

        // 优先从可选航段中获取机场
        if (parameters.available_segments && parameters.available_segments.length > 0) {
            parameters.available_segments.forEach((segment) => {
                const dep = normalizeAirportCode(segment.departure_airport);
                const arr = normalizeAirportCode(segment.arrival_airport);
                if (dep) codes.add(dep);
                if (arr) codes.add(arr);
            });
        }
        if (flights && flights.length > 0) {
            flights.forEach((flight) => {
                const dep = normalizeAirportCode(getFlightDepartureAirport(flight));
                const arr = normalizeAirportCode(getFlightArrivalAirport(flight));
                if (dep) codes.add(dep);
                if (arr) codes.add(arr);
            });
        }

        return Array.from(codes);
    }, [parameters.available_segments, flights]);

    useEffect(() => {
        // 设置表单初始值
        form.setFieldsValue({
            curfew_warning_minutes: parameters.curfew_warning_minutes || 30,
            curfew_notice_minutes: parameters.curfew_notice_minutes || 60,
        });
    }, [parameters, form]);

    // 刷新约束数据
    const refreshConstraints = () => {
        // 不预先清空：加载完成后会整体替换。清空会让主 effect 把整张表当成缺口再并发补拉一次，
        // 且空窗期内宵禁校验会静默跳过。
        if (airportCodes.length > 0) {
            loadAirportConstraints(airportCodes);
        } else {
            loadAllAirportConstraints();
        }

        message.success("约束数据刷新开始");
    };

    // 加载机场约束数据
    const loadAirportConstraints = async (codes) => {
        if (!codes || codes.length === 0) {
            console.warn("无机场代码可用，准备加载所有机场约束数据");
            loadAllAirportConstraints();
            return;
        }

        setConstraintsLoading(true);

        try {
            await ConstraintsService.loadAirportConstraints(codes, {
                setConstraintsLoading,
                setAirportConstraints,
                setConstraintsError,
            });
        } catch (error) {
            console.error("加载机场约束时出错:", error);
            message.error("加载机场约束数据失败");
        }
    };

    // 加载所有机场约束数据
    const loadAllAirportConstraints = async () => {
        setConstraintsLoading(true);

        try {
            await ConstraintsService.loadAllAirportConstraints({
                setConstraintsLoading,
                setAirportConstraints,
                setConstraintsError,
            });
        } catch (error) {
            console.error("加载所有机场约束时出错:", error);
            message.error("加载所有机场约束数据失败");
        }
    };

    // 统计宵禁规则机场数量
    const getCurfewAirportCount = () => {
        return Object.values(airportConstraints).filter((a) => (a.dep_curfew_rules && a.dep_curfew_rules.length > 0) || (a.arr_curfew_rules && a.arr_curfew_rules.length > 0)).length;
    };

    // 处理宵禁提醒设置变更
    const handleCurfewSettingChange = (field, value) => {
        // 验证值的合理性
        if (field === "curfew_warning_minutes" && value > (parameters.curfew_notice_minutes || 60)) {
            message.warning("黄色警告分钟数不能大于蓝色提示分钟数");
            return;
        }

        if (field === "curfew_notice_minutes" && value < (parameters.curfew_warning_minutes || 30)) {
            message.warning("蓝色提示分钟数不能小于黄色警告分钟数");
            return;
        }

        // 更新参数
        updateParameters({
            [field]: value,
        });

        message.success("宵禁提醒设置已更新");
    };

    // 打开宵禁规则查看器
    const openCurfewViewer = () => {
        // 不选择特定机场时，查看所有机场的宵禁规则
        setSelectedAirport(null);
        setShowCurfewModal(true);
    };

    // 打开特定机场的宵禁规则查看器
    const openAirportCurfewViewer = (airportCode) => {
        const airport = getAirportConstraint(airportConstraints, airportCode);
        if (airport) {
            setSelectedAirport(airport);
            setShowCurfewModal(true);
        }
    };

    // 打开最小过站时间查看器
    const openGroundTimeViewer = () => {
        setShowGroundTimeModal(true);
    };

    return (
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ color: "#faad14", marginRight: 8 }}>黄色警告:</span>
                            <InputNumber min={5} max={120} value={parameters.curfew_warning_minutes || 30} onChange={(value) => handleCurfewSettingChange("curfew_warning_minutes", value)} addonAfter="分钟" style={{ width: 120 }} />
                            <Tooltip title="距离宵禁时间小于此值时显示黄色警告">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: "rgba(0,0,0,0.45)", fontSize: 16 }} />
                            </Tooltip>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ color: "#1890ff", marginRight: 8 }}>蓝色提示:</span>
                            <InputNumber min={10} max={180} value={parameters.curfew_notice_minutes || 60} onChange={(value) => handleCurfewSettingChange("curfew_notice_minutes", value)} addonAfter="分钟" style={{ width: 120 }} />
                            <Tooltip title="距离宵禁时间小于此值时显示蓝色提示">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: "rgba(0,0,0,0.45)", fontSize: 16 }} />
                            </Tooltip>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col span={16}>
                        <p>
                            已加载 <strong>{Object.keys(airportConstraints).length}</strong> 个机场， 其中 <strong>{getCurfewAirportCount()}</strong> 个机场有宵禁规则
                        </p>
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                        <Space>
                            <Button onClick={openGroundTimeViewer}>查看详情</Button>
                            <Button onClick={refreshConstraints}>刷新</Button>
                        </Space>
                    </Col>
                </Row>

                {loading && (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <Spin tip="正在加载约束数据..." />
                    </div>
                )}

                {error && (
                    <div style={{ color: "red", margin: "10px 0" }}>
                        <InfoCircleOutlined /> 加载约束数据出错: {error.message || "未知错误"}
                    </div>
                )}
            </Card>

            {/* 宵禁规则查看器 */}
            <CurfewRulesViewer visible={showCurfewModal} onClose={() => setShowCurfewModal(false)} airport={selectedAirport} />

            {/* 最小过站时间查看器 */}
            <MinGroundTimeViewer visible={showGroundTimeModal} onClose={() => setShowGroundTimeModal(false)} airportConstraints={airportConstraints} />
        </div>
    );
};

/**
 * 运输需求内容组件 - 直接实现
 */
const TransportationDemandContent = ({ airportOptions, loadingAirports }) => {
    const { state, addTransportationDemand, removeTransportationDemand, updateTransportationDemand } = useScheduleEditor();
    const { parameters, schedule } = state;
    const { transportation_demands = [] } = parameters;
    const { timeZone } = useTimeZone();

    const [modalVisible, setModalVisible] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [editingDemand, setEditingDemand] = useState(null);

    // 创建专用表单实例
    const [demandForm] = Form.useForm();

    const selectOptions = React.useMemo(() => {
        if (!airportOptions || !Array.isArray(airportOptions)) {
            return [];
        }
        return airportOptions
            .map((airport) => {
                if (!airport || typeof airport !== "object") {
                    return null;
                }
                return {
                    value: airport.iata_code,
                    label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                };
            })
            .filter(Boolean);
    }, [airportOptions]);

    // 格式化时间显示，根据当前时区设置
    const formatTimeByTimeZone = (timeStr) => {
        if (!timeStr) return "";

        try {
            // 根据TIMEZONE_GUIDE规范，运输需求的首选时间统一使用UTC存储和显示
            // 不进行时区转换，直接显示存储的UTC时间
            if (timeStr.includes("T") && (timeStr.includes("Z") || timeStr.includes("+"))) {
                // 如果是完整的ISO时间字符串，提取时间部分但保持UTC
                return dayjs.utc(timeStr).format("HH:mm");
            }

            // 如果只是时间字符串 "HH:mm"，直接返回（已经是UTC）
            return timeStr;
        } catch (error) {
            console.error("格式化时间显示错误:", error);
            return timeStr;
        }
    };

    // 打开添加需求的弹窗
    const showAddDemandModal = () => {
        setEditingDemand(null);
        demandForm.resetFields();
        setModalVisible(true);
    };

    // 打开编辑需求的弹窗
    const showEditDemandModal = (demand) => {
        setEditingDemand(demand);
        // 格式化首选时间为 dayjs 对象以便表单使用
        const formData = { ...demand };
        if (formData.preferred_time && !formData.preferred_time.$d) {
            try {
                // 根据TIMEZONE_GUIDE规范，编辑时强制使用UTC时间，不考虑用户选择的时区
                // preferred_time存储的是"HH:mm"格式的UTC时间字符串
                formData.preferred_time = dayjs.utc(formData.preferred_time, "HH:mm");
            } catch (e) {
                console.error("格式化首选时间失败:", e);
            }
        }

        // 设置表单值
        demandForm.setFieldsValue(formData);
        setModalVisible(true);
    };

    // 关闭弹窗
    const handleCancel = () => {
        setModalVisible(false);
        setEditingDemand(null);
    };

    // 处理表单提交
    const handleSubmit = () => {
        setConfirmLoading(true);
        demandForm
            .validateFields()
            .then((values) => {
                // 处理首选起飞时间，确保以标准格式保存
                let processedValues = { ...values };

                if (values.preferred_time) {
                    // 确保preferred_time是标准的小时:分钟格式字符串
                    // 不存储为带时区信息的ISO字符串，这样在使用时更方便
                    try {
                        processedValues.preferred_time = values.preferred_time.format("HH:mm");
                    } catch (e) {
                        console.error("格式化首选时间错误:", e);
                        // 如果格式化失败，保留原始值
                    }
                }

                // 保留frequency字段，如果不存在则设置默认值
                if (!processedValues.frequency && editingDemand && editingDemand.frequency) {
                    processedValues.frequency = editingDemand.frequency;
                } else if (!processedValues.frequency) {
                    processedValues.frequency = 1; // 默认值
                }

                if (editingDemand) {
                    // 更新已有需求
                    const updatedDemand = {
                        ...editingDemand,
                        ...processedValues,
                    };
                    updateTransportationDemand(updatedDemand);
                    message.success("已更新运输需求");
                } else {
                    // 添加新需求
                    const newDemand = {
                        ...processedValues,
                        id: Date.now().toString(),
                    };
                    addTransportationDemand(newDemand);
                    message.success("已添加运输需求");
                }

                setModalVisible(false);
                setEditingDemand(null);
            })
            .catch((error) => {
                console.error("验证表单字段失败:", error);
                message.error("请填写所有必填字段");
            })
            .finally(() => {
                setConfirmLoading(false);
            });
    };

    const handleRemoveDemand = (id) => {
        removeTransportationDemand(id);
        message.success("已删除运输需求");
    };

    // 机场选项过滤函数
    const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) || (option?.value ?? "").toLowerCase().includes(input.toLowerCase());

    // 星期选项
    const WEEKDAY_OPTIONS = [
        { label: "周一", value: "1" },
        { label: "周二", value: "2" },
        { label: "周三", value: "3" },
        { label: "周四", value: "4" },
        { label: "周五", value: "5" },
        { label: "周六", value: "6" },
        { label: "周日", value: "7" },
    ];

    // 渲染首选日期显示
    const renderPreferredDays = (days) => {
        if (!days || days.length === 0) return null;
        // 直接显示数字，不显示中文
        return days.map((day) => day).join(", ");
    };

    return (
        <div>
            {shouldKeepPaperRotations(schedule) ? (
                <Alert type="info" showIcon message="本源计划认纸面轮班，不造运输需求。轮班号以航司文件为准。" style={{ marginBottom: 12 }} />
            ) : null}
            <Row gutter={[16, 16]} align="middle">
                <Col span={16}>
                    <p>
                        共计 <strong>{transportation_demands.length}</strong> 个运输需求
                    </p>
                </Col>
                <Col span={8} style={{ textAlign: "right" }}>
                    <Button type="primary" ghost onClick={showAddDemandModal} icon={<DatabaseOutlined />}>
                        添加运输需求
                    </Button>
                </Col>
            </Row>

            {transportation_demands && transportation_demands.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                    {transportation_demands.map((demand, index) => (
                        <Card key={demand.id} size="small" style={{ marginBottom: 8 }}>
                            <Row gutter={16}>
                                <Col span={6} style={{ textAlign: "center" }}>
                                    {demand.origin} - {demand.destination}
                                </Col>
                                <Col span={8}>
                                    <div>
                                        <Text type="secondary">首选日期:</Text>
                                        {demand.preferred_days && demand.preferred_days.length > 0 ? (
                                            renderPreferredDays(demand.preferred_days)
                                        ) : (
                                            <Text type="warning" style={{ marginLeft: 4 }}>
                                                未设置
                                            </Text>
                                        )}
                                    </div>
                                </Col>
                                <Col span={4}>
                                    <div>
                                        <Text type="secondary">首选时间:</Text>
                                        {demand.preferred_time ? (
                                            formatTimeByTimeZone(demand.preferred_time)
                                        ) : (
                                            <Text type="warning" style={{ marginLeft: 4 }}>
                                                未设置
                                            </Text>
                                        )}
                                    </div>
                                </Col>
                                <Col span={6} style={{ textAlign: "right" }}>
                                    <Space>
                                        <Button type="link" size="small" onClick={() => showEditDemandModal(demand)}>
                                            编辑
                                        </Button>
                                        <Button type="link" danger size="small" onClick={() => handleRemoveDemand(demand.id)}>
                                            删除
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: "center", padding: "20px 0", background: "#f9f9f9", borderRadius: 4, marginTop: 16 }}>
                    <Text type="secondary">暂无运输需求，请添加</Text>
                </div>
            )}

            <Modal title={editingDemand ? "编辑运输需求" : "添加运输需求"} open={modalVisible} onOk={handleSubmit} onCancel={handleCancel} confirmLoading={confirmLoading} width={700}>
                <Form form={demandForm} layout="horizontal" labelAlign="left">
                    <div className="form-inline-group">
                        <Form.Item name="origin" label="始发地" rules={[{ required: true, message: "请选择始发地" }]}>
                            <Select placeholder="请选择始发地" showSearch loading={loadingAirports} options={selectOptions} filterOption={filterOption} />
                        </Form.Item>

                        <Form.Item name="destination" label="目的地" rules={[{ required: true, message: "请选择目的地" }]}>
                            <Select placeholder="请选择目的地" showSearch loading={loadingAirports} options={selectOptions} filterOption={filterOption} />
                        </Form.Item>

                        <Form.Item name="preferred_time" label="首选起飞时间 (UTC)">
                            <UTCTimePicker format="HH:mm" style={{ width: "100%" }} showDate={false} />
                        </Form.Item>
                    </div>

                    <Form.Item name="preferred_days" label="首选执行日期" className="weekday-picker">
                        <Checkbox.Group options={WEEKDAY_OPTIONS} />
                    </Form.Item>

                    <div className="day-actions">
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                const allDays = ["1", "2", "3", "4", "5", "6", "7"];
                                demandForm.setFieldValue("preferred_days", allDays);
                            }}
                        >
                            全选
                        </Button>
                        <Button size="small" type="link" onClick={() => demandForm.setFieldValue("preferred_days", [])}>
                            清空
                        </Button>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                demandForm.setFieldValue("preferred_days", ["1", "3", "5", "7"]);
                            }}
                        >
                            一三五日
                        </Button>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                demandForm.setFieldValue("preferred_days", ["2", "4", "6"]);
                            }}
                        >
                            二四六日
                        </Button>
                    </div>

                    {/* 
                    <Form.Item name="priority" label="优先级">
                        <Select placeholder="选择优先级">
                            <Option value="high">高</Option>
                            <Option value="medium">中</Option>
                            <Option value="low">低</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="remarks" label="备注">
                        <Input.TextArea placeholder="请输入备注信息(可选)" rows={3} />
                    </Form.Item> */}
                </Form>
            </Modal>
        </div>
    );
};

export default ScheduleBasicInfo;
